import { drawerActions, drawerEventParams, eventCategories } from 'hooks/useAnalytics/constants';
import { ResourceType } from '../../types';
import { ZoneContext } from 'context/ZoneContext';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';
import { MouseEvent, useCallback, useContext } from 'react';
import { Resource, ResourceImgContainer, ResourceText, ResourceWrapper } from './DrawerResource.styled';
import { Button } from '@medly-components/core';
import { useRegionContext } from 'context/RegionContext';
import { getContentWithSearchHighlited } from 'pages/MapSearch/utils';
import useMapSearch from 'hooks/search/useMapSearch';
import { DefaultResourceThumbnail, getImageAbsoluteURL } from './utils';
import useImage from 'hooks/useImage';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { DrawerResourceInteractionEvent } from 'hooks/useAnalytics/types';

export type DrawerResource = {
    resource: ResourceType;
    stationName: string;
    position: number;
};
export const DrawerResource = ({ resource, stationName, position }: DrawerResource) => {
    const { zone, selectedStation } = useContext(ZoneContext);
    const { region } = useRegionContext();
    const { captureEvent, captureMantaCareEvent } = useAnalytics();
    const { getSearchWords } = useMapSearch();
    const searchWords = selectedStation ? getSearchWords(selectedStation.id) : [];
    const imageURL = getImageAbsoluteURL(resource.link, resource.openGraph?.images?.[0]);
    const image = useImage({
        url: imageURL,
        defaultImage: DefaultResourceThumbnail,
        imageProps: {
            testid: 'resource-image'
        }
    });

    const handleIntersect = (isInView: boolean) => {
        if (isInView) {
            captureInternalEvent('resource', 'impression');
        }
    };
    const { ref } = useIntersectionObserver(handleIntersect);

    const captureGAEvent = () => {
        captureEvent(eventCategories.DRAWER, drawerActions.CLICK_LINK, null, {
            [drawerEventParams.RESOURCE_TITLE]: resource.title,
            [drawerEventParams.RESOURCE_LINK]: resource.link,
            [drawerEventParams.STATION_ID]: selectedStation!.id,
            [drawerEventParams.STATION_DESCRIPTION]: selectedStation!.description,
            [drawerEventParams.STATION_NAME]: stationName,
            [drawerEventParams.REGION_NAME]: region.name,
            [drawerEventParams.ZONE_NAME]: zone.name
        });
    };

    const captureInternalEvent = (
        type: DrawerResourceInteractionEvent['metadata']['type'],
        engagementType: DrawerResourceInteractionEvent['metadata']['engagementType'] = 'click'
    ) => {
        captureMantaCareEvent({
            type: 'map',
            action: 'drawer_link_interaction',
            metadata: {
                region: {
                    id: region.id,
                    name: region.name
                },
                map: { name: region.map.name },
                zone: { name: zone.name },
                station: {
                    id: selectedStation!.id,
                    name: selectedStation!.name,
                    type: selectedStation!.type,
                    description: selectedStation!.description
                },
                type,
                name: resource.title,
                link: resource.link,
                position,
                engagementType
            }
        });
    };

    /**
     * Navigates to the provided link of resource in new tab
     */
    const onResourceClick = useCallback(() => {
        captureGAEvent();
        captureInternalEvent('resource');
        window.open(resource.link, '_blank');
    }, [resource.link]);

    const onLearnMoreClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        captureGAEvent();
        captureInternalEvent('resource_learn_more');
        window.open(resource.link, '_blank');
    };

    return (
        <ResourceWrapper ref={ref}>
            <Resource onClick={onResourceClick}>
                <ResourceImgContainer>{image}</ResourceImgContainer>
                <ResourceText
                    textVariant="h4"
                    dangerouslySetInnerHTML={{ __html: getContentWithSearchHighlited(resource.title || resource.link, searchWords) }}
                ></ResourceText>
                <Button variant="outlined" style={{ width: '100%', height: '4rem' }} onClick={onLearnMoreClick}>
                    Learn More
                </Button>
            </Resource>
        </ResourceWrapper>
    );
};
